import { useState } from 'react';
import { Modal } from '@mui/material';
import SettingsModal from '../../components/SettingsModal';

const ConfigValueModal = ({
  isOpen,
  onClose,
  config,
  onSave,
  isSubmitting,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Inner modal state for editing a specific config type
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedConfigKey, setSelectedConfigKey] = useState(null);
  const [selectedConfigData, setSelectedConfigData] = useState(null);

  const configValue = config?.value || {};

  // Format type name for display
  const formatTypeName = (key) => {
    if (!key) return '';
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  // Filter config data based on search term
  const getFilteredConfigData = () => {
    if (!searchTerm) return configValue;

    const searchLower = searchTerm.toLowerCase();
    const filtered = {};

    Object.entries(configValue).forEach(([key, value]) => {
      const keyMatches = key.toLowerCase().includes(searchLower);
      const descriptionMatches = value?.description?.toLowerCase().includes(searchLower);

      if (keyMatches || descriptionMatches) {
        filtered[key] = value;
      }
    });

    return filtered;
  };

  const filteredData = getFilteredConfigData();
  const configEntries = Object.entries(filteredData);
  const totalPages = Math.ceil(configEntries.length / pageSize) || 1;
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedData = configEntries.slice(startIndex, startIndex + pageSize);

  // Open edit modal for a specific config type
  const handleEditType = (key) => {
    setSelectedConfigKey(key);
    setSelectedConfigData(configValue[key]);
    setIsEditModalOpen(true);
  };

  // Close edit modal
  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedConfigKey(null);
    setSelectedConfigData(null);
  };

  // Save changes from edit modal
  const handleEditModalSave = async (key, updatedData) => {
    // Build the updated value object with all config types
    const updatedConfigValue = {
      ...configValue,
      [key]: updatedData,
    };

    // Call parent save handler with updated value
    await onSave(updatedConfigValue);
    closeEditModal();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <>
      <Modal open={isOpen} onClose={onClose}>
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full md:w-[900px] max-w-5xl overflow-hidden border border-gray-200 relative flex flex-col" style={{ height: '750px' }}>
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-bold text-secondary-800">
                  Configuration Value
                </h2>
                <p className="text-sm text-gray-500">
                  {config?.client} / {config?.company} / {config?.type}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold p-1"
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            {/* Search */}
            <div className="p-4 border-b border-gray-200">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search by type or description..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg outline-none focus:border-secondary-500"
              />
            </div>

            {/* Table */}
            <div className="flex-1 overflow-y-auto">
              <table className="min-w-full bg-white">
                <thead>
                  <tr className="bg-gray-100 border-b border-gray-200">
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                      Configuration Type
                    </th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                      Description
                    </th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                      Items Count
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="py-8 text-center text-gray-500">
                        No configuration data available
                      </td>
                    </tr>
                  ) : (
                    paginatedData.map(([key, value]) => (
                      <tr
                        key={key}
                        className="border-b border-gray-100 hover:bg-gray-50"
                      >
                        <td className="py-3 px-4">
                          <button
                            onClick={() => handleEditType(key)}
                            className="font-medium text-secondary-700 hover:text-secondary-900 hover:underline cursor-pointer text-left"
                          >
                            {formatTypeName(key)}
                          </button>
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          {value?.description || '-'}
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          {typeof value === 'object' && value !== null
                            ? Object.values(value).reduce((sum, v) => sum + (Array.isArray(v) ? v.length : 0), 0)
                            : 0}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex justify-between items-center p-4 border-t border-gray-200">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Show</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-sm text-gray-600">entries</span>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  Showing {configEntries.length > 0 ? startIndex + 1 : 0} to{' '}
                  {Math.min(startIndex + pageSize, configEntries.length)} of {configEntries.length} entries
                </span>
                {totalPages > 1 && (
                  <div className="flex gap-1">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Prev
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-1 border rounded text-sm ${
                          currentPage === page
                            ? 'bg-secondary-700 text-white border-secondary-700'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Close Button */}
            <div className="flex justify-end p-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="border border-secondary-700 text-secondary-700 py-2 px-6 rounded-full hover:bg-gray-50 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Settings Modal for editing a specific config type */}
      <SettingsModal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        onSave={handleEditModalSave}
        configKey={selectedConfigKey}
        configData={selectedConfigData}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

export default ConfigValueModal;
